import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import RadarIcon from "@mui/icons-material/Radar";
import SensorsIcon from "@mui/icons-material/Sensors";
import BookOutlinedIcon from "@mui/icons-material/BookOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import ScoreboardOutlinedIcon from "@mui/icons-material/ScoreboardOutlined";
import { NavBar } from "../customComponents";
import StatusBar from "./StatusBar";
import { useContestId } from "../../hooks/useContestId";

const Nav = (props) => {
  const { userStatus, contestStatus } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const entryPage = useSelector((state) => state?.app?.entryPage);
  const currentStage = useSelector((state) => state?.app?.currentStage);
  const hasLiveGame = useSelector((state) => state?.app?.hasLiveGame);
  const score = useSelector((state) => state?.app?.score);
  const contestType = useSelector((state) => state?.app?.contestType);
  const isKnockout = contestType === "Knockout";

  // Carry the active contest through navigation, resolved via useContestId so it
  // survives a URL that has already lost the param (recovered from session
  // storage) — the nav links self-heal rather than depend on the current URL.
  const contestId = useContestId();
  const contestQuery = contestId ? `?contest_id=${contestId}` : "";

  const getActiveTab = useCallback(
    (ctx) => {
      // A Knockout contest has no group stage — every Picks/Results link must
      // target the knockout route regardless of entry_page / current_stage.
      if (isKnockout) return "knockout";
      if (ctx === "picks") {
        // The picks page only has "group" and "knockout" tabs. entryPage can
        // also be "overview" or "results" (e.g. once an entry is submitted the
        // backend sets entry_page="results"), and those produce a dead
        // /picks/<x> link with no matching route. Clamp to a valid tab: honor
        // an explicit group/knockout, otherwise fall back to the contest stage.
        if (entryPage === "group" || entryPage === "knockout") return entryPage;
        return currentStage === "knockout" ? "knockout" : "group";
      } else if (ctx === "results") {
        // Results only has group/knockout routes. current_stage can be
        // "not_started"/"pre_contest"/etc. before kickoff, which would build a
        // dead /results/<x> link (the "No routes matched" console error).
        // Clamp: knockout once the stage reaches knockout, otherwise group.
        return currentStage === "knockout" ? "knockout" : "group";
      }
    },
    [entryPage, currentStage, isKnockout],
  );

  const navLinks = useMemo(
    () => [
      {
        title: "Overview",
        href: `/overview${contestQuery}`,
        hrefRoot: "/overview",
        icon: EmojiEventsOutlinedIcon,
      },
      {
        title: "Picks",
        href: `/picks/${getActiveTab("picks")}${contestQuery}`,
        hrefRoot: "picks",
        icon: RadarIcon,
      },
      {
        title: "Games",
        href: `/games${contestQuery}`,
        hrefRoot: "/games",
        icon: SensorsIcon,
        addLive: hasLiveGame,
      },
      {
        title: "Results",
        href: `/results/${getActiveTab("results")}${contestQuery}`,
        hrefRoot: "results",
        icon: ScoreboardOutlinedIcon,
      },
      {
        title: "Leaderboard",
        mobileTitle: "Ranks",
        href: `/leaderboard${contestQuery}`,
        hrefRoot: "/leaderboard",
        icon: WorkspacePremiumOutlinedIcon,
      },
      {
        title: "Rules",
        href: `/rules${contestQuery}`,
        hrefRoot: "/rules",
        icon: BookOutlinedIcon,
      },
    ],
    [getActiveTab, hasLiveGame, contestQuery],
  );

  // Persistent user score, sitting in the header under the title so it's exposed
  // on every page at all times. Fed by /status (app state), so it reflects the
  // latest load. Scored = banked, Missed = decided-wrong picks, Max = ceiling.
  const ScoreReadout = () => {
    const s = score || {};
    const big = isMobile ? "16px" : "22px";
    const Stat = ({ label, value, color }) => (
      <Grid container direction="column" sx={{ width: "auto", alignItems: "center" }}>
        <Typography sx={{ fontSize: big, fontWeight: 700, lineHeight: 1.15, color }}>
          {value}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: theme.branding.gray[500], whiteSpace: "nowrap", lineHeight: 1 }}
        >
          {label}
        </Typography>
      </Grid>
    );
    return (
      <Grid
        container
        direction="row"
        wrap="nowrap"
        sx={{ width: "auto", gap: isMobile ? "14px" : "28px", alignItems: "center" }}
      >
        <Stat label="Scored" value={Number(s.earned_score) || 0} color={theme.branding.cyan[600]} />
        <Stat label="Missed" value={Number(s.missed_score) || 0} color={theme.branding.red[500]} />
        <Stat label="Max" value={Number(s.max_score) || 0} color="inherit" />
      </Grid>
    );
  };

  return (
    <Grid
      container
      id="app-nav"
      sx={[
        {
          position: "sticky",
          top: 0,
          backgroundColor: theme.branding.gray[0],
          justifyContent: "center",
          borderBottom: `1px solid ${theme.branding.gray[100]}`,
          zIndex: 100,
        },
        (theme) =>
          theme.applyStyles("dark", {
            backgroundColor: theme.branding.darkBlue[800],
          }),
      ]}
    >
      <Grid
        container
        direction="column"
        spacing="16px"
        sx={{
          padding: "16px 24px 0px",
          maxWidth: theme.breakpoints.values.lg,
          width: "100%",
        }}
      >
        {isMobile ? (
          <Grid
            container
            direction="row"
            wrap="nowrap"
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <Grid
              container
              direction="row"
              wrap="nowrap"
              sx={{ gap: "8px", width: "auto", alignItems: "center" }}
            >
              <EmojiEventsOutlinedIcon
                sx={{ height: "18px", color: theme.branding.cyan[500] }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                World Cup
              </Typography>
            </Grid>
            <ScoreReadout />
          </Grid>
        ) : (
          <Grid
            container
            direction="row"
            wrap="nowrap"
            sx={{ gap: "12px", justifyContent: "space-between", alignItems: "center" }}
          >
            <Grid
              container
              direction="row"
              wrap="nowrap"
              sx={{ gap: "12px", width: "auto", alignItems: "center" }}
            >
            {/* trophy circle icon */}
            <Grid
              container
              justify="center"
              alignItems="center"
              sx={[
                {
                  width: "40px",
                  height: "40px",
                  padding: "0px 10px",
                  borderRadius: "100%",
                  background: `linear-gradient(135deg, ${theme.branding.cyan[300]} 0%, ${theme.branding.cyan[700]} 100%)`,
                  boxShadow: `0 5px 13px -3px ${theme.branding.cyan[600]}, 0 4px 6px -4px ${theme.branding.cyan[800]}`,
                },
                (theme) =>
                  theme.applyStyles("dark", {
                    boxShadow: `3px 4px 8px -3px ${theme.branding.cyan[800]}, 0 4px 6px -4px ${theme.branding.cyan[800]}`,
                  }),
              ]}
            >
              <EmojiEventsOutlinedIcon
                sx={{
                  height: "20px",
                  width: "20px",
                  color: theme.branding.gray[0],
                  alignSelf: "center",
                }}
              />
            </Grid>
            <Grid container direction="column" justify="center" spacing="1px">
              <Typography variant="h4">World Cup Bracket Challenge</Typography>
              <Typography variant="caption">SpotlightNews</Typography>
            </Grid>
            </Grid>
            <ScoreReadout />
          </Grid>
        )}
        {/* links */}
        <NavBar navLinks={navLinks} showIcons />
      </Grid>
      <StatusBar userStatus={userStatus} contestStatus={contestStatus} />
    </Grid>
  );
};

export default Nav;
